/**
 * Type-ahead user search backed by Microsoft Graph.
 * Calls /api/users/search?q= with 300ms debounce.
 */
import { useEffect, useRef, useState } from "react";
import { api, type UserSearchResult } from "../api/client";

interface Props {
  selected: UserSearchResult | null;
  onChange: (u: UserSearchResult | null) => void;
  placeholder?: string;
  excludeOids?: string[];   // hide these from results (e.g. the current user)
}

export default function UserPicker({ selected, onChange, placeholder = "Search by name or email…", excludeOids = [] }: Props) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selected) return;
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.searchUsers(q.trim());
        setResults(r.filter((u) => !excludeOids.includes(u.oid)));
        setOpen(true);
      } catch (e) {
        setError((e as Error).message);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, selected]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const click = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", click);
    return () => document.removeEventListener("mousedown", click);
  }, []);

  if (selected) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between">
        <div>
          <div className="font-medium text-slate-700">{selected.displayName}</div>
          <div className="text-slate-500 text-sm">{selected.email}</div>
        </div>
        <button
          type="button"
          onClick={() => {
            onChange(null);
            setQ("");
          }}
          className="text-liaise-navy text-sm hover:underline"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div ref={wrap} className="relative">
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:border-liaise-navy focus:ring-1 focus:ring-liaise-navy outline-none"
      />
      {loading && (
        <div className="absolute right-3 top-3 text-xs text-slate-400">Searching…</div>
      )}
      {error && <div className="text-red-600 text-sm mt-1">{error}</div>}
      {open && results.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-auto">
          {results.map((u) => (
            <li key={u.oid}>
              <button
                type="button"
                onClick={() => {
                  onChange(u);
                  setOpen(false);
                  setQ("");
                }}
                className="w-full text-left px-4 py-2.5 hover:bg-liaise-navy-50 transition"
              >
                <div className="font-medium text-slate-700">{u.displayName}</div>
                <div className="text-slate-500 text-sm">{u.email}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !loading && q.trim().length >= 2 && results.length === 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg px-4 py-3 text-sm text-slate-500">
          No matches found in Entra. Try a different name or email.
        </div>
      )}
    </div>
  );
}
